import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Loader2, Tag, ArrowRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/context/LanguageContext';
import { resolveMediaUrl } from '@/lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso, lang) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return ''; }
};

export default function BlogListPage() {
  const { lang, pickLocale, t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (activeTag) params.set('tag', activeTag);
    axios.get(`${API}/articles?${params.toString()}`)
      .then(({ data }) => setArticles(data || []))
      .finally(() => setLoading(false));
  }, [search, activeTag]);

  const allTags = Array.from(new Set(articles.flatMap((a) => a.tags || []))).slice(0, 16);

  return (
    <>
      <Helmet>
        <title>{`${lang === 'hi' ? 'रियल एस्टेट ब्लॉग' : 'Real Estate Blog'} | Shree Bajrang Developers`}</title>
        <meta name="description" content={lang === 'hi'
          ? 'जामुल–भिलाई में प्लॉट्स, घर और व्यावसायिक संपत्तियों पर विशेषज्ञ सुझाव और गाइड्स।'
          : 'Expert tips and guides on plots, houses and commercial properties in Jamul–Bhilai.'} />
      </Helmet>

      <div className="pt-20 pb-16" data-testid="blog-list-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-orange-50 via-white to-amber-50/40 border-b">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 md:py-16">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {lang === 'hi' ? 'रियल एस्टेट जर्नल' : 'Real Estate Journal'}
            </h1>
            <p className="text-base md:text-lg text-gray-600 mt-4 max-w-2xl">
              {lang === 'hi'
                ? 'जामुल–भिलाई में निवेश, RERA, घर खरीदने की प्रक्रिया और बहुत कुछ — हमारे विशेषज्ञों से।'
                : 'Insights on investing, RERA, the home-buying process and more — from our experts in Jamul–Bhilai.'}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={lang === 'hi' ? 'खोजें...' : 'Search articles...'}
                  className="pl-9 bg-white"
                  data-testid="blog-search-input"
                />
              </div>
              {activeTag && (
                <Button variant="outline" onClick={() => setActiveTag('')} data-testid="blog-clear-tag">
                  {lang === 'hi' ? 'टैग हटाएँ' : 'Clear tag'}: #{activeTag}
                </Button>
              )}
            </div>

            {allTags.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {allTags.map((tg) => (
                  <button
                    key={tg}
                    onClick={() => setActiveTag(tg === activeTag ? '' : tg)}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                      tg === activeTag
                        ? 'bg-brand-orange text-white border-brand-orange'
                        : 'bg-white text-gray-700 hover:border-brand-orange'
                    }`}
                    data-testid={`blog-tag-${tg}`}
                  >
                    #{tg}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Article grid */}
        <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10 md:py-14">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-orange" /></div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              {lang === 'hi' ? 'कोई आर्टिकल नहीं मिला।' : 'No articles found.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a, idx) => {
                const title = pickLocale(a, 'title', a.title_en);
                const excerpt = pickLocale(a, 'excerpt', a.excerpt_en);
                return (
                  <Link
                    key={a.id}
                    to={`/blog/${a.slug}`}
                    className="group bg-white border rounded-2xl overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1"
                    data-testid={`blog-card-${idx}`}
                  >
                    <div className="aspect-[16/9] bg-gradient-to-br from-orange-100 to-amber-100 overflow-hidden">
                      {a.cover_image ? (
                        <img
                          src={resolveMediaUrl(a.cover_image)}
                          alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-brand-orange/40 font-heading text-4xl">SBD</div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <span>{formatDate(a.published_at || a.created_at, lang)}</span>
                        {a.tags?.length > 0 && (
                          <>
                            <span>·</span>
                            <span className="flex items-center gap-1"><Tag className="h-3 w-3" /> {a.tags[0]}</span>
                          </>
                        )}
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-gray-900 leading-snug group-hover:text-brand-orange transition-colors line-clamp-2">
                        {title}
                      </h3>
                      {excerpt && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-3">{excerpt}</p>
                      )}
                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {(a.tags || []).slice(0, 2).map((tg) => (
                            <Badge key={tg} variant="outline" className="text-xs">#{tg}</Badge>
                          ))}
                        </div>
                        <span className="text-brand-orange flex items-center gap-1 text-sm font-medium">
                          {lang === 'hi' ? 'पढ़ें' : 'Read'} <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
